import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  kullaniciFamilileriniGetir,
  yeniAileOlusturService,
  kodlaAileyeKatilService,
  aktifAileDegistirService,
} from "../services/aileSwitcherService";
import { adminPermissions, defaultPermissions } from "../utils/permissions";
import { useLanguage } from "../i18n/LanguageContext";
import { listLabel } from "../utils/i18nOptions";
import { appAlert } from "./AppDialog";
import FamilyInvitePanel from "./FamilyInvitePanel";
import { formatFamilyInviteInput } from "../services/familyInviteService";

/* ── Renk seçenekleri ── */
const RENKLER = [
  "#6366f1","#ec4899","#f59e0b","#10b981",
  "#06b6d4","#8b5cf6","#ef4444","#3b82f6",
  "#84cc16","#f97316",
];

/* ── İkon seçenekleri ── */
const IKONLAR = ["🏠","👨‍👩‍👧","💼","🌳","🏖️","🏡","❤️","⭐","🎯","🌟","🏕️","🎪"];

/* ─────────────────────────────────────────
   ONAY POPUP
───────────────────────────────────────── */
function OnayPopup({ hedefAile, aktifAile, onOnayla, onIptal, t }) {
  const portal = (
    <div
      style={{
        position:"fixed",inset:0,zIndex:10100,
        background:"rgba(0,0,0,0.6)",
        display:"flex",alignItems:"center",justifyContent:"center",
        padding:"0 20px",
        backdropFilter:"blur(6px)",
      }}
      onClick={onIptal}
    >
      <div
        className="aileSwitcherOnayKart"
        onClick={e=>e.stopPropagation()}
      >
        <div className="aileSwitcherOnayIkonlar">
          <div className="aileSwitcherOnayIkon" style={{background:`${aktifAile?.renk}22`}}>
            <span>{aktifAile?.ikon||"🏠"}</span>
          </div>
          <div className="aileSwitcherOnayOk">→</div>
          <div className="aileSwitcherOnayIkon" style={{background:`${hedefAile?.renk}22`}}>
            <span>{hedefAile?.ikon||"🏠"}</span>
          </div>
        </div>
        <div className="aileSwitcherOnayBaslik">{t("familySwitcher.confirmTitle")}</div>
        <div className="aileSwitcherOnaySub">
          <strong>{aktifAile?.name}</strong> → <strong>{hedefAile?.name}</strong>
          <br/>{t("familySwitcher.confirmSub")}
        </div>
        <div className="aileSwitcherOnayBtnler">
          <button className="aileSwitcherOnayIptal" onClick={onIptal}>{t("common.cancel")}</button>
          <button className="aileSwitcherOnayOnayla" onClick={onOnayla}>{t("familySwitcher.switch")}</button>
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(portal, document.body);
}

/* ─────────────────────────────────────────
   YENİ AİLE OLUŞTUR FORMU
───────────────────────────────────────── */
function YeniAileFormu({ user, onTamamlandi, onGeri, limitKontrol, t }) {
  const [ad, setAd] = useState("");
  const [ikon, setIkon] = useState("🏠");
  const [renk, setRenk] = useState("#6366f1");
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState("");

  const kaydet = async () => {
    if (!limitKontrol?.(t("familySwitcher.createFamily"))) return;
    if (!ad.trim()) { setHata(t("familySwitcher.familyNameRequired")); return; }
    setYukleniyor(true); setHata("");
    try {
      const sonuc = await yeniAileOlusturService({ aileAdi: ad, ikon, renk, user });
      onTamamlandi(sonuc);
    } catch(e) { setHata(e.message); }
    finally { setYukleniyor(false); }
  };

  return (
    <div className="aileSwitcherForm">
      <div className="aileSwitcherFormBaslik">
        <button className="aileSwitcherGeriBtn" onClick={onGeri}>←</button>
        {t("familySwitcher.createFamily")}
      </div>

      {/* Önizleme */}
      <div className="aileSwitcherOnizleme">
        <div className="aileSwitcherOnizlemeIkon" style={{background:`${renk}22`,border:`2px solid ${renk}44`}}>
          {ikon}
        </div>
        <div className="aileSwitcherOnizlemeAd">{ad || t("familySwitcher.familyName")}</div>
      </div>

      {/* Ad */}
      <div className="aileSwitcherFormGrup">
        <label className="darkLabel">{t("familySwitcher.familyName")}</label>
        <input className="darkInput" placeholder={t("familySwitcher.familyNamePlaceholder")} value={ad} onChange={e=>setAd(e.target.value)} maxLength={30}/>
      </div>

      {/* İkon */}
      <div className="aileSwitcherFormGrup">
        <label className="darkLabel">{t("familySwitcher.icon")}</label>
        <div className="aileSwitcherIkonGrid">
          {IKONLAR.map(i=>(
            <button key={i} className={`aileSwitcherIkonBtn${ikon===i?" secili":""}`} onClick={()=>setIkon(i)}>{i}</button>
          ))}
        </div>
      </div>

      {/* Renk */}
      <div className="aileSwitcherFormGrup">
        <label className="darkLabel">{t("familySwitcher.color")}</label>
        <div className="aileSwitcherRenkGrid">
          {RENKLER.map(r=>(
            <button
              key={r}
              className={`aileSwitcherRenkBtn${renk===r?" secili":""}`}
              style={{background:r}}
              onClick={()=>setRenk(r)}
              aria-label={r}
            />
          ))}
        </div>
      </div>

      {hata && <div className="aileSwitcherHata">{hata}</div>}
      <button className="primaryButton" onClick={kaydet} disabled={yukleniyor}>
        {yukleniyor ? t("familySwitcher.creating") : t("familySwitcher.createFamily")}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────
   KODLA KATIL FORMU
───────────────────────────────────────── */
function KodlaKatilFormu({ user, onTamamlandi, onGeri, limitKontrol, t }) {
  const [kod, setKod] = useState("");
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState("");

  const katil = async () => {
    if (!limitKontrol?.(t("familySwitcher.joinFamily"))) return;
    if (!kod.trim()) { setHata(t("familySwitcher.codeRequired")); return; }
    setYukleniyor(true); setHata("");
    try {
      const sonuc = await kodlaAileyeKatilService({ katilmaKodu: kod, user });
      onTamamlandi(sonuc);
    } catch(e) { setHata(e.message); }
    finally { setYukleniyor(false); }
  };

  return (
    <div className="aileSwitcherForm">
      <div className="aileSwitcherFormBaslik">
        <button className="aileSwitcherGeriBtn" onClick={onGeri}>←</button>
        {t("familySwitcher.joinByCode")}
      </div>
      <div className="aileSwitcherKodAciklama">
        {t("familySwitcher.joinHint")}
      </div>
      <div className="aileSwitcherFormGrup">
        <label className="darkLabel">{t("familySwitcher.inviteCode")}</label>
        <input
          className="darkInput aileSwitcherKodInput"
          placeholder="FP-XXXXXXX"
          value={kod}
          onChange={e=>setKod(formatFamilyInviteInput(e.target.value))}
          maxLength={10}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          style={{letterSpacing:"0.15em",textAlign:"center",fontSize:20,fontWeight:800}}
        />
      </div>
      {hata && <div className="aileSwitcherHata">{hata}</div>}
      <button className="primaryButton" onClick={katil} disabled={yukleniyor}>
        {yukleniyor ? t("familySwitcher.checking") : t("familySwitcher.joinFamily")}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────
   ANA AİLE SWITCHER DROPDOWN
───────────────────────────────────────── */
export default function AileSwitcher({ user, profil, familyId, familyName, yoneticiMi = false, onAileDegisti, onKapat, isPremium = false, premiumConfig = {}, onPremiumLimitHit, isSuperAdmin = false }) {
  const { t, tList } = useLanguage();
  const [aileler, setAileler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [sekme, setSekme] = useState("liste"); // liste | yeniAile | kodlaKatil | davet
  const [onayHedef, setOnayHedef] = useState(null);
  const [gorunenAlan, setGorunenAlan] = useState(() => {
    if (typeof window === "undefined") return { width: 480, left: 0 };
    return {
      width: window.visualViewport?.width || window.innerWidth || 480,
      left: window.visualViewport?.offsetLeft || 0,
    };
  });
  const adminVirtualFamily = { familyId: "__admin__", name: "Admin", code: "", ownerUid: user?.uid || "", ikon: "🛡️", renk: "#6366f1", rol: "Süper Admin", isAdminVirtual: true };
  const sheetRef = useRef(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    const guncelle = () => {
      setGorunenAlan({
        width: viewport?.width || window.innerWidth || 480,
        left: viewport?.offsetLeft || 0,
      });
    };
    guncelle();
    viewport?.addEventListener("resize", guncelle);
    viewport?.addEventListener("scroll", guncelle);
    window.addEventListener("resize", guncelle);
    return () => {
      viewport?.removeEventListener("resize", guncelle);
      viewport?.removeEventListener("scroll", guncelle);
      window.removeEventListener("resize", guncelle);
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    kullaniciFamilileriniGetir(user.uid).then(data => {
      setAileler(data);
      setYukleniyor(false);
    });
  }, [user?.uid]);

  // Aktif aile bilgisi
  const aktifAile = aileler.find(a => a.familyId === familyId) || {
    name: familyName, ikon: "🏠", renk: "#6366f1", familyId
  };

  // Aktif kullanıcı sonradan aile yöneticisi yapıldığında aileler[] içindeki eski rol
  // bilgisi stale kalabilir. Paylaş yetkisi her zaman canlı profil/yoneticiMi
  // bilgisinden hesaplanır; aile değiştirme listesinin eski verisi butonu pasif bırakmaz.
  const aktifAileYoneticiMi =
    yoneticiMi === true ||
    profil?.aileYetkisi === "Aile Yöneticisi" ||
    aktifAile?.ownerUid === user?.uid ||
    aktifAile?.rol === "Aile Yöneticisi";

  const aileLimitKontrol = (baslik) => {
    if (isPremium) return true;
    const limit = Number(premiumConfig?.limits?.kullaniciAile ?? 1);
    if (limit > 0 && aileler.length >= limit) {
      onPremiumLimitHit?.(
        baslik,
        aileler.length,
        limit,
        [t("familySwitcher.premium.multiFamily"), t("familySwitcher.premium.unlimitedJoin"), t("familySwitcher.premium.quickSwitch")],
        t("familySwitcher.limitMessage", { limit }),
        [t("familySwitcher.plus.multiFamily"), t("familySwitcher.plus.unlimitedMembers"), t("familySwitcher.plus.unlimitedRecords")]
      );
      return false;
    }
    return true;
  };

  // Aile seçildi → onay popup
  const aileSec = (aile) => {
    if (aile?.isAdminVirtual) {
      onAileDegisti?.({ isAdminMode: true, familyId: "__admin__", familyName: "Admin", rol: "Süper Admin", permissions: adminPermissions });
      onKapat();
      return;
    }
    if (aile.familyId === familyId) return;
    setOnayHedef(aile);
  };

  // Onaylandı → geçiş yap
  const onaylaVeGec = async () => {
    if (!onayHedef) return;
    try {
      if (onayHedef?.isAdminVirtual) {
        onAileDegisti?.({ isAdminMode: true, familyId: "__admin__", familyName: "Admin", rol: "Süper Admin", permissions: adminPermissions });
        setOnayHedef(null);
        onKapat();
        return;
      }
      const sonuc = await aktifAileDegistirService({
        uid: user.uid,
        familyId: onayHedef.familyId,
        families: aileler,
        currentFamilyId: familyId,
      });
      await onAileDegisti?.({
        familyId: onayHedef.familyId,
        familyName: onayHedef.name,
        familyCode: onayHedef.code || "",
        ownerUid: onayHedef.ownerUid || "",
        rol: sonuc.rol,
        permissions: sonuc.permissions,
        families: aileler,
      });
    } catch(e) { appAlert(e.message); }
    setOnayHedef(null);
    onKapat();
  };

  // Yeni aile / katılma tamamlandı
  const tamamlandi = (sonuc) => {
    // Yeni aile oluşturunca ownerUid === user.uid → Yönetici
    // Aileye katılınca → Üye
    const rol = sonuc.ownerUid === user?.uid ? "Aile Yöneticisi" : "Aile Üyesi";
    const permissions = rol === "Aile Yöneticisi" ? adminPermissions : defaultPermissions;
    onAileDegisti({
      familyId: sonuc.familyId,
      familyName: sonuc.familyName,
      familyCode: sonuc.familyCode || "",
      ownerUid: sonuc.ownerUid || "",
      rol,
      permissions,
    });
    onKapat();
  };

  const portal = (
    <>
      {/* Overlay */}
      <div
        style={{
          position:"fixed",inset:0,zIndex:10000,
          background:"rgba(0,0,0,0.55)",
          backdropFilter:"blur(4px)",
        }}
        onClick={onKapat}
      />

      {/* Sheet — telefonda full width, web'de max 480px ortada */}
      <div
        ref={sheetRef}
        className="aileSwitcherSheet"
        style={{
          position:"fixed",
          top:0,
          left:`${gorunenAlan.left + (gorunenAlan.width / 2)}px`,
          transform:"translateX(-50%)",
          width:`${Math.min(480, Math.max(0, gorunenAlan.width))}px`,
          maxWidth:"480px",
          boxSizing:"border-box",
          zIndex:10001,
          maxHeight:"var(--aile-switcher-max-height, 85vh)",
          overflowY:"auto",
          "--primary-color": profil?.calendarColor || "#6366f1",
        }}
        onClick={e=>e.stopPropagation()}
      >
        <div className="aileSwitcherHandle"/>

        {/* ── LİSTE SEKMESİ ── */}
        {sekme === "liste" && (
          <>
            <div className="aileSwitcherSheetBaslik">
              {t("familySwitcher.myFamilies")}
              <span className="aileSwitcherSayi">{aileler.length + (isSuperAdmin ? 1 : 0)} / 10</span>
            </div>

            {yukleniyor ? (
              <div className="aileSwitcherYukleniyor">{t("common.loading")}</div>
            ) : (!isSuperAdmin && aileler.length === 0) ? (
              <div className="aileSwitcherBos">{t("familySwitcher.empty")}</div>
            ) : (
              <div className="aileSwitcherListe">
                {[...(isSuperAdmin ? [adminVirtualFamily] : []), ...aileler].map(aile => {
                  const aktif = aile.isAdminVirtual ? false : aile.familyId === familyId;
                  return (
                    <div
                      key={aile.familyId}
                      className={`aileSwitcherRow${aktif?" aktif":""}`}
                      onClick={()=>aileSec(aile)}
                    >
                      <div
                        className="aileSwitcherRowIkon"
                        style={{background:`${aile.renk}20`,border:`1.5px solid ${aile.renk}40`}}
                      >
                        {aile.ikon}
                      </div>
                      <div className="aileSwitcherRowBilgi">
                        <div className="aileSwitcherRowAd">{aile.name}</div>
                        <div className="aileSwitcherRowRol">{aile.isAdminVirtual ? (t("adminDashboard.superAdminRole") || "Süper Admin") : listLabel(tList, "const.familyAuthorities", aile.rol)}</div>
                      </div>
                      {aktif
                        ? <span className="aileSwitcherAktifBadge">{t("familySwitcher.active")}</span>
                        : <span className="aileSwitcherChevron">›</span>
                      }
                    </div>
                  );
                })}
              </div>
            )}

            <div className="aileSwitcherAyrac"/>

            {/* Alt butonlar */}
            <div className="aileSwitcherAltBtnler">
              <button
                className="aileSwitcherAltBtn"
                onClick={()=>{ if (!aileLimitKontrol(t("familySwitcher.createFamily"))) return; setSekme("yeniAile"); }}
              >
                <div className="aileSwitcherAltBtnIkon" style={{background:"rgba(99,102,241,0.12)"}}>
                  <span style={{fontSize:20}}>＋</span>
                </div>
                <div className="aileSwitcherAltBtnMetin">
                  <div className="aileSwitcherAltBtnBaslik">{t("familySwitcher.createFamily")}</div>
                  <div className="aileSwitcherAltBtnSub">{t("familySwitcher.createSub")}</div>
                </div>
              </button>
              <button
                className="aileSwitcherAltBtn"
                onClick={()=>{ if (!aileLimitKontrol(t("familySwitcher.joinFamily"))) return; setSekme("kodlaKatil"); }}
              >
                <div className="aileSwitcherAltBtnIkon" style={{background:"rgba(16,185,129,0.12)"}}>
                  <span style={{fontSize:20}}>🔑</span>
                </div>
                <div className="aileSwitcherAltBtnMetin">
                  <div className="aileSwitcherAltBtnBaslik">{t("familySwitcher.joinByCode")}</div>
                  <div className="aileSwitcherAltBtnSub">{t("familySwitcher.enterInviteCode")}</div>
                </div>
              </button>

              <button
                className={`aileSwitcherAltBtn ${!aktifAileYoneticiMi ? "disabledShareBtn" : ""}`}
                onClick={()=>{
                  if (!aktifAileYoneticiMi) return;
                  setSekme("davet");
                }}
                disabled={!aktifAileYoneticiMi}
              >
                <div className="aileSwitcherAltBtnIkon aileSwitcherInviteIcon">
                  <span style={{fontSize:20}}>✦</span>
                </div>

                <div className="aileSwitcherAltBtnMetin">
                  <div className="aileSwitcherAltBtnBaslik">{t("familySwitcher.secureInvite")}</div>
                  <div className="aileSwitcherAltBtnSub">
                    {aktifAileYoneticiMi
                      ? t("familySwitcher.secureInviteSub")
                      : t("familySwitcher.adminOnlyInvite")}
                  </div>
                </div>
              </button>
            </div>

            {!isPremium && Number(premiumConfig?.limits?.kullaniciAile ?? 1) > 0 && aileler.length >= Number(premiumConfig?.limits?.kullaniciAile ?? 1) && (
              <div className="aileSwitcherLimitUyari">
                {t("familySwitcher.limitWarning")}
              </div>
            )}
          </>
        )}

        {/* ── YENİ AİLE SEKMESİ ── */}
        {sekme === "yeniAile" && (
          <YeniAileFormu user={user} onTamamlandi={tamamlandi} onGeri={()=>setSekme("liste")} limitKontrol={aileLimitKontrol} t={t}/>
        )}

        {/* ── KODLA KATIL SEKMESİ ── */}
        {sekme === "kodlaKatil" && (
          <KodlaKatilFormu user={user} onTamamlandi={tamamlandi} onGeri={()=>setSekme("liste")} limitKontrol={aileLimitKontrol} t={t}/>
        )}

        {/* ── GÜVENLİ DAVET SEKMESİ ── */}
        {sekme === "davet" && (
          <div className="aileSwitcherForm familyInviteSwitcherWrap">
            <div className="aileSwitcherFormBaslik">
              <button className="aileSwitcherGeriBtn" onClick={()=>setSekme("liste")}>←</button>
              {t("familySwitcher.secureInvite")}
            </div>
            <p className="familyInviteSwitcherIntro">{t("familySwitcher.secureInviteIntro")}</p>
            <FamilyInvitePanel
              familyId={aktifAile?.familyId || familyId}
              familyName={aktifAile?.name || familyName}
              source="family_switcher"
              variant="compact"
              showHeading={false}
              accentColor={profil?.calendarColor || "#6366f1"}
            />
          </div>
        )}
      </div>

      {/* Onay popup */}
      {onayHedef && (
        <OnayPopup
          hedefAile={onayHedef}
          aktifAile={aktifAile}
          onOnayla={onaylaVeGec}
          onIptal={()=>setOnayHedef(null)}
          t={t}
        />
      )}
    </>
  );

  return ReactDOM.createPortal(portal, document.body);
}
